// DataTable (Components, BL-189 consolidation): records, one a row, to open and to tick (base.css's
// .flw-table, as Accounting › Invoices and Payments and refunds drew them; Customers › All customers'
// and the price book's tables folded in). A head on the ink rule, rows on hairlines, numbers to the
// right; it scrolls in its own box when it's wider than the page, or folds (narrow="fold"). One column
// is the row's way in: with hrefOf its words are the row's link (600, underlined in ink) and a click
// anywhere in the row opens it (the app's own way with onOpen: a job's page remembers where it was
// opened from); with onOpen alone they're a button that opens the record in place (only the words
// answer); a row this person can't open isn't a link, and with neither (a chat) no row opens. A cell is
// words, drawn from its DataCell: muted or strong, a state word (DESIGN.md §2: muted, dashed under while
// it waits, red for a problem), links (an invoice a payment paid), lines under it, struck through, red
// for a problem (a loss, a price not set), dashed under at the table's size for what waits on someone,
// a figure beside its Meter (each technician's for each hour). What each record's columns say is the
// caller's (rows/money-columns.ts, rows/customer-columns.ts). An action column (`kind: 'action'`) ends a
// row with what can be done to it here, a Link (Banking's Unmatch), or several that wrap (the price
// book's asks: Add to the book, Just this job, Turn down); its head is for screen readers alone. A control column (`kind: 'control'`) puts a catalogued field in each row: a SelectField from a
// list or a TextField typed by hand, named for screen readers by its row (the ledger export's "QuickBooks
// Online account for 1100 Accounts receivable"); at a figure's width with `fit` (the pay register's
// amounts, several to a row); a typed one that saves as it's left (`onBlur`: Books setup's chart names) is
// left on Enter too, so it saves once. A lead (a picture) comes before the way in, ticks (select) before that, and
// the row open beside the list (current) has the ink mark on its left. Under the rows, a total (`foot`:
// the same columns, strong, on the ink rule), the first few then Show all (`first`) or the next page on
// asking (`more`), and a muted line while the next answer is on its way (`busyText`). With
// stickyRowHeader the row's head (the day that names it) stays in view while a wide table scrolls
// sideways on a phone. With onSort (Inbox › Lists) each head is a button that sorts by its column, the
// sorted one saying which way with ↑ or ↓ (and aria-sort); the caller sorts. `look` keeps what a page had
// that DESIGN.md §4 doesn't rule (Seth, 4 Nov): quiet (All customers) the page's line height, the name at
// 14 with a faint underline that inks on hover, tabular figures only in the columns that ask (`figures`);
// hover (the price book) a list's line height, the name underlined only on hover, the row tinted on
// hover, cells on the middle, no tabular figures. Reads only --dtable-*; Link, Meter, Checkbox and Field
// read their own.
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Checkbox } from '../Checkbox/Checkbox.tsx';
import { SelectField, TextField, type SelectOption } from '../Field/Field.tsx';
import { Link } from '../Link/Link.tsx';
import { Meter, type MeterProps } from '../Meter/Meter.tsx';
import { leaveOnEnter } from './leave-on-enter.ts';
import './DataTable.css';

/** A state word, drawn as DESIGN.md §2 says: ink (the default), muted, dashed under for waiting, red. */
export interface DataWord {
  word: string;
  tone?: 'ink' | 'muted' | 'wait' | 'bad';
}

/** A line under a cell's words: muted (13 px), or red and strong for a problem ("3 days late"). */
export interface DataLine {
  text: ReactNode;
  tone?: 'bad';
}

/** What can be done to a row here, drawn as a Link at its end (Unmatch). */
export interface DataAction {
  text: string;
  onPress: () => void;
  /** Its name for screen readers where its words repeat down the rows ("Unmatch Phone service"). */
  label?: string;
  isDisabled?: boolean;
}

/** A control column's field: a choice from `options` (SelectField), or without them words typed (TextField). */
export interface DataControl {
  /** Its name for screen readers, from its row ("QuickBooks Online account for 1100 Accounts receivable"). */
  label: string;
  value: string;
  onChange: (value: string) => void;
  options?: SelectOption[];
  placeholder?: string;
  /** The keyboard a phone shows: decimal for an amount (the pay register's gross). */
  inputMode?: 'decimal' | 'numeric';
  /** Leaving the field (a name that saves once it's typed, as the chart's); Enter leaves it too. */
  onBlur?: () => void;
  isDisabled?: boolean;
}

export interface DataCell {
  /** The cell's words. */
  text?: ReactNode;
  /**
   * muted: a value that isn't one yet ("Not issued"); strong: the figure that matters (what's owed); bad:
   * a problem, red and strong (a job that lost money, a price not set, an overdue amount); wait: what
   * waits on someone, dashed under, at the table's size (a draft's words).
   */
  look?: 'muted' | 'strong' | 'bad' | 'wait';
  /** A state word in place of the words. */
  word?: DataWord;
  /** Links, one after another with commas (the invoices a payment paid); without href, the words alone. */
  links?: readonly { id: string; text: string; href?: string | null }[];
  /** Lines under it; a line may be a state word. */
  lines?: readonly (DataLine | DataWord)[];
  /** Voided: struck through (and muted, where it isn't a link). */
  struck?: boolean;
  /** A bar before the words, the figure beside it (the catalogued Meter: value, max, marks, fill). */
  meter?: MeterProps;
  /** An action column's cell: its Link, in place of words. */
  action?: DataAction;
  /** An action column's cell with several: their Links one after another, wrapping, in place of words. */
  actions?: readonly DataAction[];
  /** A control column's cell: its field, in place of words. */
  control?: DataControl;
}

export interface DataColumn<R> {
  id: string;
  label: string;
  /** A figure: to the right, never wrapping. */
  align?: 'end';
  /** The row's head (th scope=row): the number or the day that names the row. */
  rowHeader?: boolean;
  /** This column's words are the row's way in (its link with hrefOf, its button with onOpen alone). */
  way?: boolean;
  /**
   * action: the row's action at its end (the cell's `action`, a Link); the label is for screen readers.
   * control: a field in each row (the cell's `control`); the total draws its words (a sum), never a field.
   */
  kind?: 'action' | 'control';
  /**
   * A control column at a figure's width, its field to the right (the pay register's amounts, several
   * to a row), in place of half the table.
   */
  fit?: 'figure';
  /** true: never wraps (a phone number, a day); false: a row head that wraps (a long name). */
  nowrap?: boolean;
  /** Tabular figures, where the table's look doesn't set them for every column (quiet: a phone, an amount). */
  figures?: boolean;
  cell: (row: R) => DataCell;
}

/** The next page of a list, read on asking: its words ("Show 50 more of 120"), while it reads, and why it didn't. */
export interface MoreRows {
  label: string;
  onPress: () => void;
  isBusy?: boolean;
  error?: string | null;
}

/** Ticks: one a row, and one in the head for them all. */
export interface DataSelect<R> {
  selected: readonly string[];
  onSelect: (ids: string[]) => void;
  /** The row's name for its tick ("Tick 50 gal gas water heater"). */
  nameOf: (row: R) => string;
}

/** The column the rows are sorted by, and which way. */
export interface DataSort {
  column: string;
  dir: 'asc' | 'desc';
}

export interface DataTableProps<R extends { id: string }> {
  /** The table's name for screen readers, where nothing names it ("Accounts without a match"). */
  label?: string;
  columns: readonly DataColumn<R>[];
  rows: readonly R[];
  /** Where a row opens, or null for one this person can't open; without it (a chat) no row opens. */
  hrefOf?: (row: R) => string | null;
  /** Opens a row the app's own way (a job's page, remembering where it was opened from), in place of
   * following its link; the link stays for a new tab. Without hrefOf, the way in is a button that opens
   * the record in place, and only it answers. */
  onOpen?: (row: R) => void;
  /** What the page had that §4 doesn't rule: ink (the default), quiet (All customers), hover (the price book). */
  look?: 'ink' | 'quiet' | 'hover';
  /** The total under the rows: the same columns, strong, on the ink rule; never a link. */
  foot?: R | null;
  /** The next page, on asking. */
  more?: MoreRows | null;
  /** The first so many rows, then Show all N; the caller keys the table to start again. */
  first?: number;
  /** The next answer is on its way: the rows stay, and this line says so ("Searching…"). */
  busyText?: string;
  /**
   * With no rows, the sentence it says between dashed rules ("No invoices here. Try another filter.");
   * without it, nothing (the region says it).
   */
  empty?: string;
  /** A picture before the way in; its head is for screen readers ("Picture"). */
  lead?: { label: string; cell: (row: R) => ReactNode };
  /** Ticks, one a row. */
  select?: DataSelect<R>;
  /** The row open beside the list: the ink mark on its left. */
  current?: string | null;
  /**
   * scroll (the default): it keeps its columns and scrolls; fold: at 700 or narrower each row folds to
   * its tick, picture and way in, its cells lines under them.
   */
  narrow?: 'scroll' | 'fold';
  /**
   * auto (the default): each column as wide as its words. fixed: the first column takes a set share of
   * the width and the others split the rest evenly, their words wrapping, so tables with the same columns
   * one under another line up (Who may do what's role columns across its groups).
   */
  layout?: 'auto' | 'fixed';
  /** The rowHeader column stays in view while the table scrolls sideways (a month's days, on a phone). */
  stickyRowHeader?: boolean;
  /** The column the rows are sorted by, said in its head (↑ or ↓, and aria-sort). */
  sort?: DataSort | null;
  /** Each head a button that sorts by its column (an action column's never); which way is the caller's. */
  onSort?: (column: string) => void;
}

/** A row opens as its own link does, wherever in the row it's clicked (not on another link or control). */
const openRow = (e: MouseEvent<HTMLTableRowElement>): void => {
  if ((e.target as Element).closest('a, button, input, select, textarea, label')) return;
  e.currentTarget.querySelector<HTMLAnchorElement>('a[data-open]')?.click();
};

/** Nothing to say: folded, the cell leaves its label out too. */
const isEmpty = (c: DataCell) =>
  (c.text == null || c.text === '') &&
  !c.word &&
  !c.links?.length &&
  !c.action &&
  !c.actions?.length &&
  !c.control &&
  !c.lines?.length &&
  !c.meter;

const Word = ({ w }: { w: DataWord }) => (
  <span className="fl-dtable__word" data-tone={w.tone ?? 'ink'}>
    {w.word}
  </span>
);

function Words({
  c,
  way,
  href,
  onOpen,
  current,
  fit,
}: {
  c: DataCell;
  way: boolean;
  href: string | null;
  onOpen?: (() => void) | undefined;
  current: boolean;
  fit?: 'figure' | undefined;
}) {
  const struck = c.struck ? '' : undefined;
  if (way)
    return href ? (
      <a
        className="fl-dtable__way"
        data-open=""
        data-struck={struck}
        href={href}
        onClick={
          onOpen
            ? (e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                onOpen();
              }
            : undefined
        }
      >
        {c.text}
      </a>
    ) : onOpen ? (
      <button
        type="button"
        className="fl-dtable__way"
        data-struck={struck}
        aria-current={current ? 'true' : undefined}
        onClick={onOpen}
      >
        {c.text}
      </button>
    ) : (
      <b className="fl-dtable__strong" data-struck={struck}>
        {c.text}
      </b>
    );
  if (c.action)
    return (
      <Link
        onPress={c.action.onPress}
        isDisabled={!!c.action.isDisabled}
        {...(c.action.label ? { label: c.action.label } : {})}
      >
        {c.action.text}
      </Link>
    );
  if (c.actions?.length)
    return (
      <span className="fl-dtable__acts">
        {c.actions.map((a) => (
          <Link
            key={a.text}
            size="hit-on-phone"
            onPress={a.onPress}
            isDisabled={!!a.isDisabled}
            {...(a.label ? { label: a.label } : {})}
          >
            {a.text}
          </Link>
        ))}
      </span>
    );
  if (c.control) {
    const k = c.control;
    const common = { label: k.label, hideLabel: true, value: k.value, onChange: k.onChange };
    const field = k.options ? (
      <SelectField {...common} options={k.options} {...(k.isDisabled ? { isDisabled: true } : {})} />
    ) : (
      <TextField
        {...common}
        {...(k.placeholder ? { placeholder: k.placeholder } : {})}
        {...(k.inputMode ? { inputMode: k.inputMode } : {})}
        {...(k.onBlur ? { onBlur: k.onBlur } : {})}
        {...(fit === 'figure' ? { width: 'number' as const } : {})}
        {...(k.isDisabled ? { isDisabled: true } : {})}
      />
    );
    return fit === 'figure' ? <span className="fl-dtable__fit">{field}</span> : field;
  }
  if (c.word) return <Word w={c.word} />;
  if (c.links)
    return (
      <>
        {c.links.map((l, k) => (
          <span key={l.id}>
            {k > 0 && ', '}
            {l.href ? (
              <a className="fl-dtable__link" href={l.href}>
                {l.text}
              </a>
            ) : (
              l.text
            )}
          </span>
        ))}
      </>
    );
  if (c.look === 'muted') return <span className="fl-dtable__muted">{c.text}</span>;
  if (c.look === 'strong') return <b className="fl-dtable__strong">{c.text}</b>;
  if (c.look === 'bad') return <b className="fl-dtable__bad">{c.text}</b>;
  if (c.look === 'wait') return <span className="fl-dtable__wait">{c.text}</span>;
  return <>{c.text}</>;
}

/** A cell's body: its words (beside its meter, when it has one), then the lines under them. */
function Body({
  c,
  way,
  href,
  onOpen,
  current,
  fit,
}: {
  c: DataCell;
  way: boolean;
  href: string | null;
  onOpen?: (() => void) | undefined;
  current: boolean;
  fit?: 'figure' | undefined;
}) {
  const words = <Words c={c} way={way} href={href} onOpen={onOpen} current={current} fit={fit} />;
  return (
    <>
      {c.meter ? (
        <span className="fl-dtable__per">
          <Meter {...c.meter} />
          <span>{words}</span>
        </span>
      ) : (
        words
      )}
      {c.lines?.map((l, i) =>
        'word' in l ? (
          <span key={i} className="fl-dtable__sub">
            <Word w={l} />
          </span>
        ) : (
          <span key={i} className="fl-dtable__sub" data-tone={l.tone}>
            {l.text}
          </span>
        ),
      )}
    </>
  );
}

export function DataTable<R extends { id: string }>({
  label,
  columns,
  rows,
  hrefOf,
  onOpen,
  look = 'ink',
  foot,
  more,
  first,
  busyText,
  empty,
  lead,
  select,
  current,
  narrow = 'scroll',
  layout = 'auto',
  stickyRowHeader,
  sort,
  onSort,
}: DataTableProps<R>) {
  const [all, setAll] = useState(false);
  const fold = narrow === 'fold';
  // a table wider than its box scrolls in it: the box is then a named region the keyboard can reach
  // (axe scrollable-region-focusable), as LineTable's is; when it fits, no extra tab stop
  const wrap = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);
  useEffect(() => {
    const el = wrap.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setScrolls(el.scrollWidth > el.clientWidth + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, [rows.length]);
  // an action column with several actions in a row: it takes their width, and they wrap
  const acts = new Set(
    columns
      .filter((col) => col.kind === 'action' && rows.some((r) => col.cell(r).actions?.length))
      .map((col) => col.id),
  );
  // a row's cells: the row's head a th, figures to the right; the total's way column is only words
  const cells = (r: R, href: string | null, ways: boolean) =>
    columns.map((col) => {
      const c = col.cell(r);
      const body = (
        <Body
          c={c}
          way={ways && !!col.way}
          href={href}
          onOpen={onOpen ? () => onOpen(r) : undefined}
          current={ways && current != null && current === r.id}
          fit={col.fit}
        />
      );
      const num = col.align === 'end' ? '' : undefined;
      // the total has no action of its own; under a control column it says its sum, never a field
      if (col.kind === 'action')
        return (
          <td key={col.id} data-action="" data-acts={acts.has(col.id) ? '' : undefined}>
            {ways ? body : null}
          </td>
        );
      if (col.kind === 'control') {
        const k = ways ? col.cell(r).control : undefined;
        return (
          <td
            key={col.id}
            data-control=""
            data-fit={col.fit}
            data-num={num}
            {...(k?.onBlur && !k.options ? { onKeyDown: leaveOnEnter } : {})}
          >
            {ways || !col.cell(r).control ? body : null}
          </td>
        );
      }
      const data = {
        'data-num': col.align === 'end' ? '' : undefined,
        'data-nowrap': col.nowrap === true ? '' : undefined,
        'data-wrap': col.nowrap === false ? '' : undefined,
        'data-figures': col.figures ? '' : undefined,
        // folded, a cell is a line with its label, and an empty one isn't drawn
        'data-label': fold && !col.rowHeader && !col.way ? col.label : undefined,
        'data-empty': fold && isEmpty(c) ? '' : undefined,
      };
      return col.rowHeader ? (
        <th key={col.id} scope="row" {...data}>
          {body}
        </th>
      ) : (
        <td key={col.id} {...data}>
          {body}
        </td>
      );
    });
  if (!rows.length && empty)
    return (
      <p className="fl-dtable__empty" role="status">
        {empty}
      </p>
    );
  const shown = all || first == null ? rows : rows.slice(0, first);
  const on = new Set(select?.selected ?? []);
  const allTicked = rows.length > 0 && rows.every((r) => on.has(r.id));
  const tick = (id: string, to: boolean) => {
    const n = new Set(on);
    if (to) n.add(id);
    else n.delete(id);
    select?.onSelect(rows.filter((r) => n.has(r.id)).map((r) => r.id));
  };
  return (
    <div
      className="fl-dtable"
      data-look={look === 'ink' ? undefined : look}
      data-narrow={fold ? 'fold' : undefined}
      data-layout={layout === 'fixed' ? 'fixed' : undefined}
      data-marks={current !== undefined ? '' : undefined}
      data-sticky-row-header={stickyRowHeader ? '' : undefined}
    >
      {rows.length ? (
        <div
          ref={wrap}
          className="fl-dtable__wrap"
          role={scrolls ? 'region' : undefined}
          aria-label={scrolls ? (label ?? 'Table') : undefined}
          tabIndex={scrolls ? 0 : undefined}
        >
          <table className="fl-dtable__t" {...(label ? { 'aria-label': label } : {})}>
            <thead>
              <tr>
                {select ? (
                  <th className="fl-dtable__ck" scope="col">
                    <Checkbox
                      isSelected={allTicked}
                      aria-label={allTicked ? 'Clear every tick' : `Tick all ${rows.length}`}
                      onChange={(v) => select.onSelect(v ? rows.map((r) => r.id) : [])}
                    >
                      {''}
                    </Checkbox>
                  </th>
                ) : null}
                {lead ? (
                  <th className="fl-dtable__lead" scope="col">
                    <span className="fl-dtable__sr">{lead.label}</span>
                  </th>
                ) : null}
                {columns.map((col) => {
                  const dir = sort?.column === col.id ? sort.dir : null;
                  return (
                    <th
                      key={col.id}
                      scope="col"
                      data-num={col.align === 'end' ? '' : undefined}
                      data-action={col.kind === 'action' ? '' : undefined}
                      data-acts={acts.has(col.id) ? '' : undefined}
                      aria-sort={dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    >
                      {col.kind === 'action' ? (
                        <span className="fl-dtable__sr">{col.label}</span>
                      ) : onSort ? (
                        <button type="button" className="fl-dtable__sort" onClick={() => onSort(col.id)}>
                          {col.label}
                          {dir ? <span aria-hidden="true">{dir === 'asc' ? ' ↑' : ' ↓'}</span> : null}
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const href = hrefOf?.(r) ?? null;
                return (
                  <tr
                    key={r.id}
                    data-open={href ? '' : undefined}
                    data-current={current != null && current === r.id ? '' : undefined}
                    data-ticked={on.has(r.id) ? '' : undefined}
                    onClick={href ? openRow : undefined}
                  >
                    {select ? (
                      <td className="fl-dtable__ck">
                        <Checkbox
                          isSelected={on.has(r.id)}
                          aria-label={`Tick ${select.nameOf(r)}`}
                          onChange={(v) => tick(r.id, v)}
                        >
                          {''}
                        </Checkbox>
                      </td>
                    ) : null}
                    {lead ? <td className="fl-dtable__lead">{lead.cell(r)}</td> : null}
                    {cells(r, href, true)}
                  </tr>
                );
              })}
            </tbody>
            {foot ? (
              <tfoot>
                <tr>
                  {select ? <td className="fl-dtable__ck" /> : null}
                  {lead ? <td className="fl-dtable__lead" /> : null}
                  {cells(foot, null, false)}
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      ) : null}
      {rows.length > shown.length ? (
        <p className="fl-dtable__all">
          <Link onPress={() => setAll(true)}>Show all {rows.length}</Link>
        </p>
      ) : null}
      {more ? (
        <p className="fl-dtable__more">
          <Link onPress={more.onPress} isDisabled={!!more.isBusy}>
            {more.isBusy ? 'Reading…' : more.label}
          </Link>
          {more.error ? (
            <span className="fl-dtable__alert" role="alert">
              {more.error}
            </span>
          ) : null}
        </p>
      ) : null}
      {busyText ? (
        <p className="fl-dtable__busy" role="status">
          {busyText}
        </p>
      ) : null}
    </div>
  );
}
